import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { config } from '../../config/config';

const MultiSelectDropdown = ({ selectedManagers, onManagerSelect, onManagerRemove }) => {
  const [managers, setManagers] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const token = useSelector(state => state.auth.token);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchManagers = async () => {
      try {
        const response = await fetch(`${config.apiUrl}/api/users/project-managers`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();
        setManagers(data.users);
      } catch (error) {
        console.error('Error fetching managers:', error);
      }
    };

    fetchManagers();
  }, [token]);

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    // Add event listener when dropdown is open
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    // Clean up the event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div 
        className="border border-gray-200 px-4 py-2.5 cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedManagers.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {selectedManagers.map(manager => (
              <div 
                key={manager._id}
                className="flex items-center bg-blue-50 rounded-full pl-2 pr-3 py-1"
              >
                <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs mr-2">
                  {getInitials(manager.profile.firstName, manager.profile.lastName)}
                </div>
                <span className="text-sm text-blue-800">
                  {manager.profile.firstName} {manager.profile.lastName}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onManagerRemove(manager._id);
                  }}
                  className="ml-2 text-blue-500 hover:text-blue-700"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        ) : (
          <span className="text-gray-500">Select team members...</span>
        )}
      </div>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {managers.map(manager => {
            const isSelected = selectedManagers.some(m => m._id === manager._id);
            return (
              <div
                key={manager._id}
                className={`px-4 py-2 cursor-pointer hover:bg-gray-50 flex items-center ${
                  isSelected ? 'bg-blue-50' : ''
                }`}
                onClick={() => {
                  if (!isSelected) {
                    onManagerSelect(manager);
                    setIsOpen(false);
                  } else {
                    onManagerRemove(manager._id);
                  }
                }}
              >
                <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm mr-3">
                  {getInitials(manager.profile.firstName, manager.profile.lastName)}
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {manager.profile.firstName} {manager.profile.lastName}
                  </div>
                  <div className="text-xs text-gray-500">
                    {manager.profile.company.position}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown; 